<template>
  <div
    v-show="hasAnyReviews"
    class=""
  >
    <!-- Header -->
    <div class="flex items-center justify-between mb-4 sm:mb-6">
      <h2
        class="dark:text-content-inverse font-bold sm:text-2xl text-content-level-900 text-xl tracking-tight"
      >
        {{ $t('commerce.storeReviewsSlider.ratingAndReviews') }}
      </h2>

      <NuxtLink
        v-if="seeMoreLink"
        :to="seeMoreLink"
        class="bg-surface-level-100 cursor-pointer dark:bg-surface-level-800 dark:hover:bg-surface-level-700 dark:text-content-level-300 font-medium gap-1.5 hover:bg-surface-level-200 inline-flex items-center px-3 py-1.5 rounded-full sm:gap-2 sm:px-4 sm:py-2 sm:text-sm text-content-level-700 text-xs transition-colors"
      >
        <svg
          class="h-3.5 sm:h-4 sm:w-4 w-3.5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M19 14l-7 7m0 0l-7-7m7 7V3"
          /></svg
        >{{ $t('commerce.storeReviewsSlider.seeMore') }}
      </NuxtLink>
    </div>

    <!-- Source toggle -->
    <div
      v-if="hasNativeReviews && hasGoogleReviews"
      class="flex gap-2 mb-4 sm:mb-6"
    >
      <button
        type="button"
        class="cursor-pointer font-medium gap-1.5 inline-flex items-center px-4 py-2 rounded-full text-sm transition-colors"
        :class="[
          activeSource === ReviewSource.NATIVE
            ? 'bg-primary text-content-inverse'
            : 'bg-surface-level-100 text-content-level-700 hover:bg-surface-level-200 dark:bg-surface-level-800 dark:text-content-level-300 dark:hover:bg-surface-level-700'
        ]"
        @click="showNativeReviews"
      >
        {{ $t('commerce.storeReviewsSlider.mealzReviews') }}
      </button>

      <button
        type="button"
        class="cursor-pointer font-medium gap-1.5 inline-flex items-center px-4 py-2 rounded-full text-sm transition-colors"
        :class="[
          activeSource === ReviewSource.GOOGLE
            ? 'bg-primary text-content-inverse'
            : 'bg-surface-level-100 text-content-level-700 hover:bg-surface-level-200 dark:bg-surface-level-800 dark:text-content-level-300 dark:hover:bg-surface-level-700'
        ]"
        @click="showGoogleReviews"
      >
        <svg
          class="h-4 w-4"
          viewBox="0 0 24 24"
        >
          <path
            fill="currentColor"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
          />

          <path
            fill="currentColor"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />

          <path
            fill="currentColor"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />

          <path
            fill="currentColor"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          /></svg
        >{{ $t('commerce.storeReviewsSlider.googleReviews') }}
      </button>
    </div>

    <!-- Content: fixed rating card + scrollable reviews on mobile, grid on lg -->
    <div class="flex flex-col gap-4 lg:gap-4 lg:grid lg:grid-cols-5 lg:items-stretch">
      <!-- Rating summary card (fixed) -->
      <div
        class="bg-surface-canvas border border-border-level-200 dark:bg-surface-level-800 dark:border-border-level-700 lg:col-span-1 lg:flex lg:flex-col lg:justify-center p-3 rounded-2xl shrink-0 text-center w-full"
      >
        <span
          class="dark:text-content-inverse font-bold sm:text-5xl text-4xl text-content-level-900"
        >
          {{ activeAverageRating.toFixed(1) }}
        </span>

        <div class="flex gap-0.5 items-center justify-center mt-1.5 sm:mt-2">
          <svg
            v-for="star in 5"
            :key="`summary-star-${star}`"
            class="h-4 sm:h-5 sm:w-5 w-4"
            :class="
              star <= Math.round(activeAverageRating)
                ? 'text-warning-400'
                : 'text-content-level-300 dark:text-content-level-600'
            "
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
            />
          </svg>
        </div>

        <span
          class="block dark:text-content-level-400 mt-1.5 sm:mt-2 sm:text-sm text-content-level-500 text-xs"
        >
          {{ $t('commerce.storeReviewsSlider.valueRatings', { count: activeTotalRatings }) }}</span
        >
      </div>

      <!-- Review cards (scrollable on mobile/tablet, grid on lg) -->
      <div
        class="[-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden flex gap-4 items-stretch lg:col-span-4 lg:grid lg:grid-cols-3 lg:overflow-x-visible lg:pb-0 min-w-0 overflow-x-auto pb-2 scrollbar-none"
      >
        <div
          v-for="(review, index) in displayedReviews"
          :key="
            review.id ??
            ('google_maps_uri' in review ? review.google_maps_uri : null) ??
            `review-${index}`
          "
          class="bg-surface-canvas border border-border-level-200 dark:bg-surface-level-800 dark:border-border-level-700 flex flex-col lg:min-w-0 lg:w-auto p-4 rounded-2xl shrink-0 sm:w-[20rem] w-[18rem]"
        >
          <!-- Review text -->
          <div class="flex-1 overflow-hidden">
            <p
              class="dark:text-content-inverse leading-6 review-text-clamp text-content-level-900 text-sm"
            >
              {{
                $t('commerce.storeReviewsSlider.value', {
                  value: review.review || review.title || 'Great experience!'
                })
              }}
            </p>
          </div>

          <!-- Rating + author + date -->
          <div
            class="border-border-level-100 border-t dark:border-border-level-700 mt-4 pt-4 space-y-2"
          >
            <div class="flex gap-0.5 items-center">
              <svg
                v-for="star in 5"
                :key="`review-star-${review.id ?? ('google_maps_uri' in review ? review.google_maps_uri : null) ?? index}-${star}`"
                class="h-3.5 sm:h-4 sm:w-4 w-3.5"
                :class="
                  star <= review.rating
                    ? 'text-content-level-900 dark:text-content-inverse'
                    : 'text-content-level-300 dark:text-content-level-600'
                "
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                />
              </svg>
            </div>

            <div class="flex gap-3 items-center justify-between">
              <span
                class="dark:text-content-level-300 font-medium min-w-0 text-content-level-700 text-sm truncate"
              >
                {{ review.user?.name || $t('commerce.storeReviewsSlider.anonymousReviewer') }}
              </span>

              <span class="dark:text-content-level-400 shrink-0 text-content-level-500 text-xs">
                {{ formatReviewDate(review) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import type { GoogleReviewEntry, StoreReview } from '~/services/api/generated'
import { ReviewSource } from '~/enums'
import { formatRelativeTime } from '~/utils/formatRelativeTime'

const props = defineProps<{
  /** Native reviews displayed by the slider. */
  reviews: StoreReview[]
  /** Google reviews displayed when available. */
  googleReviews?: GoogleReviewEntry[]
  /** Destination for the see-more link. */
  seeMoreLink?: string
  /** Total native review count. */
  totalRatings?: number
  /** Total Google review count. */
  googleTotalRatings?: number
}>()

const activeSource = ref<ReviewSource>(ReviewSource.NATIVE)

const hasNativeReviews = computed(() => props.reviews.length > 0)
const hasGoogleReviews = computed(() => (props.googleReviews?.length ?? 0) > 0)
const hasAnyReviews = computed(() => hasNativeReviews.value || hasGoogleReviews.value)

const displayedReviews = computed(() => {
  const source =
    activeSource.value === ReviewSource.GOOGLE ? (props.googleReviews ?? []) : props.reviews
  return source.slice(0, 3)
})

const activeAverageRating = computed(() => {
  if (displayedReviews.value.length === 0) return 0
  const sum = displayedReviews.value.reduce((acc, review) => acc + review.rating, 0)
  return sum / displayedReviews.value.length
})

const activeTotalRatings = computed(() => {
  if (activeSource.value === ReviewSource.GOOGLE) return props.googleTotalRatings ?? 0
  return props.totalRatings ?? 0
})

const switchSource = (source: ReviewSource) => {
  activeSource.value = source
}

const showNativeReviews = () => {
  switchSource(ReviewSource.NATIVE)
}
const showGoogleReviews = () => {
  switchSource(ReviewSource.GOOGLE)
}

const formatDate = (value?: string | null) => {
  if (value === null || value === undefined || value.length === 0) return ''
  return new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit'
  }).format(new Date(value))
}

const formatReviewDate = (review: StoreReview | GoogleReviewEntry) => {
  const reviewedAt = review.reviewed_at
  const dateValue =
    reviewedAt !== null && reviewedAt !== undefined && reviewedAt.length > 0
      ? reviewedAt
      : review.created_at

  if ('source' in review && review.source === 'google') {
    return formatRelativeTime(dateValue)
  }

  return formatDate(dateValue)
}
</script>

<style scoped>
.review-text-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
